import React, { useRef } from 'react'
import { cn } from '@amusnet/react-ui'
import { useScaleToFit } from '@hooks'

interface FeatureOptionProps {
    id: string
    onClick: () => void
    isDisabled: boolean
    borderStyle: React.CSSProperties
    symbolBackgroundStyles: React.CSSProperties
    width: string
    height: string
    imageStyle: React.CSSProperties
    label: string
    labelFontSize: string
    labelColor?: string
    amountText: string
    currency: string
    priceColor?: string
    portraitViewport: boolean
}

// One card owns its own scale so each amount fits its own box. The amount element is
// content-hugging (`w-fit`), so measuring it against the fixed card gives the real text
// width regardless of the applied scale — no feedback loop. The scale is applied via
// `transform` with a bottom origin so amounts stay bottom-aligned as they shrink.
const FeatureOption: React.FC<FeatureOptionProps> = ({
    id,
    onClick,
    isDisabled,
    borderStyle,
    symbolBackgroundStyles,
    width,
    height,
    imageStyle,
    label,
    labelFontSize,
    labelColor,
    amountText,
    currency,
    priceColor,
    portraitViewport,
}) => {
    const cardRef = useRef<HTMLDivElement>(null)
    const amountRef = useRef<HTMLDivElement>(null)
    const titleRef = useRef<HTMLDivElement>(null)
    const scale = useScaleToFit(cardRef, amountRef, [amountText, portraitViewport])
    const titleScale = useScaleToFit(cardRef, titleRef, [label, portraitViewport])

    return (
        <div
            id={id}
            style={{ ...borderStyle, width, height, pointerEvents: isDisabled ? 'none' : 'auto' }}
            onClick={onClick}
            className={cn('cursor-pointer box-border rounded-[10px] w-full touch-manipulation')}
        >
            {isDisabled && (
                <div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        top: '0px',
                        left: '0px',
                        height: '100%',
                        backgroundColor: '#7F7F7F',
                        opacity: 0.4,
                        borderRadius: '10px',
                        touchAction: 'manipulation',
                    }}
                ></div>
            )}
            <div
                ref={cardRef}
                style={{ ...symbolBackgroundStyles, flexDirection: 'column' }}
                className={cn(
                    'h-full bg-gradient-to-b rounded-[8px] flex overflow-hidden items-center touch-manipulation'
                )}
            >
                <div className={cn('flex items-center justify-center w-[240px] touch-manipulation', 'h-[180px]')}>
                    <div
                        style={{ ...imageStyle, backgroundSize: 'auto', touchAction: 'manipulation' }}
                        className="w-full h-full object-cover rounded-t-[8px]"
                    />
                </div>
                <div
                    className={cn(
                        'leading-[100%] font-medium text-center touch-manipulation flex flex-col justify-between items-center',
                        'flex-1 text-[24px]'
                    )}
                >
                    <div
                        ref={titleRef}
                        style={{
                            fontSize: labelFontSize,
                            color: labelColor,
                            transform: `scale(${titleScale})`,
                            transformOrigin: 'top center',
                        }}
                        className={cn(
                            'font-semibold touch-manipulation leading-tight',
                            'min-h-[36px] flex items-center justify-center text-center whitespace-pre w-fit mx-auto px-[5px]'
                        )}
                    >
                        {label}
                    </div>
                    <div
                        ref={amountRef}
                        style={{ transform: `scale(${scale})`, transformOrigin: 'bottom center', color: priceColor }}
                        className="font-medium touch-manipulation whitespace-nowrap uppercase w-fit mx-auto mt-auto leading-[normal]"
                    >
                        <span className={cn('leading-[normal] font-black touch-manipulation text-[40px]')}>
                            {amountText}
                        </span>
                        {currency}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FeatureOption
